import clsx from 'clsx'
import React, { useEffect, useState } from 'react'
import { BiChevronRight } from 'react-icons/bi'
import { useLocation } from 'react-router'
import { handleLabelNameChanges } from 'src/cms/helpers/automation'
import { KTSVG } from '../../../helpers'
import { isMenuActive } from './menuActive'
import { useLayout } from '../../core'

type Props = {
  to: string
  title: string
  icon?: string
  fontIcon?: string
  hasBullet?: boolean
  /** Locally resolved icon (see menuIcons.tsx). Takes precedence over `icon`/`fontIcon`. */
  iconNode?: React.ReactNode
  /**
   * Controlled open state. When provided, the parent owns which group is open
   * (AsideMenuMain keeps a single one expanded) and must handle `onToggle`.
   * When omitted the group falls back to its own internal state.
   */
  isOpen?: boolean
  onToggle?: () => void
  /**
   * The current page lives in this group. Set by AsideMenuMain, which can see
   * the child routes -- a child may sit under a different prefix than the
   * parent's own alias, so `to` alone cannot detect it.
   */
  isBranchActive?: boolean
}

const AsideMenuItemWithSub: React.FC<Props> = ({
  children,
  to,
  title,
  icon,
  fontIcon,
  hasBullet,
  iconNode,
  isOpen,
  onToggle,
  isBranchActive = false,
}) => {
  const { pathname } = useLocation()
  const { config } = useLayout()
  const { aside } = config

  const isControlled = typeof isOpen === 'boolean'
  const [selfOpen, setSelfOpen] = useState(false)
  const isExpanded = isControlled ? Boolean(isOpen) : selfOpen

  const isOwnRouteActive = isMenuActive(pathname, to)
  const isActive = isOwnRouteActive || isBranchActive

  useEffect(() => {
    // Controlled mode: AsideMenuMain already opens the active group.
    if (!isControlled && isActive) {
      setSelfOpen(true)
    }
  }, [pathname, isActive, isControlled])

  const handleToggle = () => {
    if (isControlled) {
      onToggle?.()
      return
    }
    setSelfOpen((prev) => !prev)
  }

  return (
    <div
      // Deliberately NOT using Metronic's `menu-accordion` / `here show` /
      // `data-kt-menu-trigger` classes: open state is React's, and those bring
      // base rules (plus MenuComponent's own class toggling) that fight it.
      className={clsx('menu-item gibl-menu-group', {
        'gibl-menu-group--open': isExpanded,
        'gibl-menu-group--active': isActive,
      })}
      data-cy={`${handleLabelNameChanges(title)}-menu`}
    >
      <span
        // `active` (the filled pill) is only for the group's OWN route; a group
        // that merely contains the current page gets the softer
        // `gibl-menu-group--active` treatment instead.
        className={clsx('menu-link gibl-menu-link gibl-menu-link--parent', {
          active: isOwnRouteActive,
        })}
        data-cy={`${handleLabelNameChanges(title)}-menu-link`}
        onClick={handleToggle}
        title={title}
      >
        {iconNode && <span className='menu-icon gibl-menu-icon'>{iconNode}</span>}
        {!iconNode && hasBullet && (
          <span className='menu-bullet gibl-menu-bullet'>
            <span className='bullet bullet-dot'></span>
          </span>
        )}
        {!iconNode && icon && aside.menuIcon === 'svg' && (
          <span className='menu-icon gibl-menu-icon'>
            <KTSVG path={icon} className='svg-icon-2' />
          </span>
        )}
        {!iconNode && fontIcon && (
          <span className='menu-icon gibl-menu-icon'>
            <i className={clsx('bi fs-3', fontIcon)}></i>
          </span>
        )}
        <span className='menu-title'>{title}</span>
        <span className={clsx('menu-arrow gibl-menu-arrow', { 'gibl-menu-arrow--open': isExpanded })}>
          <BiChevronRight />
        </span>
      </span>
      <div className={clsx('menu-sub gibl-menu-sub', { show: isExpanded })}>{children}</div>
    </div>
  )
}

export { AsideMenuItemWithSub }
